import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { botApi, BotTrack, BotTrackStatus, BotPublishMode } from '../../api/admin.api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';

/** 발행 방식 버튼 목록 (트랙 B 전용) */
const PUBLISH_MODES: { label: string; value: BotPublishMode }[] = [
  { label: '승인 후 발행', value: 'APPROVAL' },
  { label: '자동 발행', value: 'AUTO' },
];

/** 발행 방식 값이 없을 때의 안전 측 기본값 — 서버 기본값과 같다 (spec §4.9.1) */
const DEFAULT_PUBLISH_MODE: BotPublishMode = 'APPROVAL';

/** 발행 시각 허용 범위 (서버에서 강제, spec Q-2) */
const SCHEDULE_MIN = '06:00';
const SCHEDULE_MAX = '23:00';

/** 연속 실패일 색상 기준 */
const FAILURE_WARN_DAYS = 1;
const FAILURE_DANGER_DAYS = 3;

const AUTO_MODE_CONFIRM = '자동 발행으로 바꿀까요?\n앞으로 트랙 B 설문이 승인 없이 바로 올라가요.';

const getFailureColor = (days: number) => {
  if (days >= FAILURE_DANGER_DAYS) return 'text-red-600';
  if (days >= FAILURE_WARN_DAYS) return 'text-orange-600';
  return 'text-gray-700';
};

const getErrorMessage = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback;

interface BotTrackCardProps {
  track: BotTrack;
  label: string;
  status: BotTrackStatus;
  /** 봇 전체(마스터)가 꺼져 있으면 트랙이 켜져 있어도 발행하지 않는다 */
  masterEnabled: boolean;
  /** 트랙 B 승인 대기 → 「자동발행 대기」 탭으로 이동 */
  onGoPending?: () => void;
}

export const BotTrackCard = ({ track, label, status, masterEnabled, onGoPending }: BotTrackCardProps) => {
  const queryClient = useQueryClient();
  const [time, setTime] = useState(status.scheduleTime);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const isEmpathy = track === 'EMPATHY';
  const publishMode = status.publishMode ?? DEFAULT_PUBLISH_MODE;

  /* 서버 값이 바뀌면 입력값 동기화 */
  useEffect(() => {
    setTime(status.scheduleTime);
  }, [status.scheduleTime]);

  /* 재조회 Promise 를 돌려줘서 isPending 이 재조회까지 덮게 한다 — 옛 값이 보이는 동안 버튼이 다시 켜지지 않도록 */
  const handleSuccess = () => {
    setErrorMessage(null);
    return queryClient.invalidateQueries({ queryKey: ['botStatus'] });
  };

  /* 🔴 카드 스위치는 이 트랙 키만 바꾼다 — 마스터 토글(PUT /toggle) 호출 금지 (spec §4.7.1) */
  const toggleMutation = useMutation({
    mutationFn: (enabled: boolean) => botApi.toggleTrack(track, enabled),
    onSuccess: handleSuccess,
    onError: (err) => setErrorMessage(getErrorMessage(err, '상태를 바꾸지 못했어요. 다시 시도해 주세요.')),
  });

  const scheduleMutation = useMutation({
    mutationFn: (nextTime: string) => botApi.updateTrackSchedule(track, nextTime),
    onSuccess: handleSuccess,
    onError: (err) => setErrorMessage(getErrorMessage(err, '시각을 저장하지 못했어요. 다시 시도해 주세요.')),
  });

  const publishModeMutation = useMutation({
    mutationFn: (mode: BotPublishMode) => botApi.setPublishMode(track, mode),
    onSuccess: handleSuccess,
    onError: (err) => setErrorMessage(getErrorMessage(err, '발행 방식을 바꾸지 못했어요. 다시 시도해 주세요.')),
  });

  /* ── 핸들러 ── */
  const handleSaveTime = () => {
    if (!time || time === status.scheduleTime) return;
    scheduleMutation.mutate(time);
  };

  const handlePublishModeChange = (mode: BotPublishMode) => {
    if (mode === publishMode) return;
    if (mode === 'AUTO' && !window.confirm(AUTO_MODE_CONFIRM)) return;
    publishModeMutation.mutate(mode);
  };

  /* ── "오늘" 줄 ── */
  const renderToday = () => {
    if (isEmpathy && status.todayPending > 0) {
      const pendingText = `⏳ 승인 대기 ${status.todayPending}건 보기 →`;
      if (!onGoPending) return <span className="text-orange-600 font-medium">{pendingText}</span>;
      return (
        <Button variant="link" size="sm" className="h-auto px-0 font-medium text-orange-600" onClick={onGoPending}>
          {pendingText}
        </Button>
      );
    }
    if (status.todayPosted) return <span className="text-green-600 font-medium">✅ 발행했어요</span>;
    return <span className="text-gray-400">아직 발행 전이에요</span>;
  };

  const rowLabelClass = 'w-10 shrink-0 text-xs text-gray-500';
  const switchId = `bot-${track}-enabled`;
  const timeInputId = `bot-${track}-schedule`;

  return (
    <Card className="p-4">
      <CardContent className="p-0 space-y-2.5">
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm font-semibold text-gray-900">{label}</div>
          {!masterEnabled && status.enabled && <span className="text-xs text-gray-400">봇 전체를 켜면 발행해요</span>}
        </div>

        {/* 상태 */}
        <div className="flex items-center gap-3">
          <label htmlFor={switchId} className={rowLabelClass}>
            상태
          </label>
          <Switch
            id={switchId}
            checked={status.enabled}
            onCheckedChange={(checked) => toggleMutation.mutate(checked)}
            disabled={toggleMutation.isPending}
          />
          <span className={`text-sm font-medium ${status.enabled ? 'text-green-600' : 'text-gray-400'}`}>
            {status.enabled ? '켜짐' : '꺼짐'}
          </span>
        </div>

        {/* 발행 시각 */}
        <div className="flex flex-wrap items-center gap-3">
          <label htmlFor={timeInputId} className={rowLabelClass}>
            발행
          </label>
          <input
            id={timeInputId}
            type="time"
            aria-label={`${label} 발행 시각`}
            value={time}
            min={SCHEDULE_MIN}
            max={SCHEDULE_MAX}
            onChange={(e) => setTime(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <Button
            size="sm"
            onClick={handleSaveTime}
            disabled={!time || time === status.scheduleTime || scheduleMutation.isPending}
          >
            {scheduleMutation.isPending ? '저장 중…' : '저장'}
          </Button>
          <span className="text-xs text-gray-400">
            {SCHEDULE_MIN}~{SCHEDULE_MAX} 사이로 고를 수 있어요
          </span>
        </div>

        {/* 발행 방식 — 트랙 B 전용 */}
        {isEmpathy && (
          <div className="flex items-center gap-3">
            <span className={rowLabelClass}>방식</span>
            <div className="flex gap-2">
              {PUBLISH_MODES.map((m) => (
                <Button
                  key={m.value}
                  variant={publishMode === m.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handlePublishModeChange(m.value)}
                  disabled={publishModeMutation.isPending}
                >
                  {m.label}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* 오늘 */}
        <div className="flex items-center gap-3 text-sm">
          <span className={rowLabelClass}>오늘</span>
          {renderToday()}
        </div>

        {errorMessage && <div className="text-xs text-red-600">{errorMessage}</div>}

        <div className="flex gap-4 pt-2 border-t text-xs text-gray-500">
          <span>
            연속 실패{' '}
            <span className={`font-semibold ${getFailureColor(status.consecutiveFailureDays)}`}>
              {status.consecutiveFailureDays}일
            </span>
          </span>
          <span>
            발행한 설문 <span className="font-semibold text-gray-700">{status.totalSurveys}개</span>
          </span>
        </div>
      </CardContent>
    </Card>
  );
};
